import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Underwriter, useUnderwritersQuery } from '../../graphql/types';
import { debounce, delay } from 'lodash';
import { Autocomplete, ClickAwayListener, Stack, TextField } from '@mui/material';
import AssetLogo from '../AssetLogo';
import { FormikProps } from 'formik';
import { Box } from '@mui/system';
import { Values as MCAValues } from '../merchants/AddMerchantCashAdvance';
import { Values as OfferValues } from '../offers/AddRehabOffers';
import McaUnderwriterWidget from 'sections/merchants/McaUnderwriterWidget';

interface Props<FormValues extends MCAValues | OfferValues> {
  id: string;
  disabled: boolean;
  funderId: string;
  formik: FormikProps<FormValues>;
  isMca?: boolean;
  onDelete?: () => void;
}

export const SecondaryFunderAutocomplete = <FormValues extends MCAValues | OfferValues>({ id, disabled, funderId, formik, onDelete }: Props<FormValues>) => {
  const { errors, touched, values, handleBlur, setFieldValue } = formik;
  const [showInfo, setShowInfo] = useState<boolean>(disabled || !!values.secondaryFunders?.[funderId]?.lender?.id);
  const inputReference = useRef<HTMLInputElement>(null);
  const [lenders, setUnderwriters] = useState<readonly Underwriter[]>([]);
  const [lenderInputValue, setUnderwriterInputValue] = useState<string>('');

  const {
    loading: lendersLoading,
    data: lendersData,
    refetch: lendersRefetch
  } = useUnderwritersQuery({
    variables: {},
    fetchPolicy: 'network-only',
    skip: disabled
  });

  const lendersFetch = useMemo(
    () =>
      debounce(
        (request: { input: string }) =>
          lendersRefetch({ search: request.input }).then((rq) => setUnderwriters((rq.data.underwriters.data as Underwriter[]) || [])),
        200
      ),
    [lendersRefetch]
  );

  useEffect(() => setUnderwriters((lendersData?.underwriters.data as Underwriter[]) || []), [lendersData?.underwriters.data, lendersLoading]);

  useEffect(() => {
    if (disabled) return;
    lendersFetch({ input: lenderInputValue });
  }, [lenderInputValue, lendersFetch, disabled]);

  // Filter out primary funder and already selected secondary funders
  const getAvailableUnderwriters = (allUnderwriters: readonly Underwriter[]) => {
    const primaryFunderId = values.funder?.id;
    const selectedSecondaryIds = Object.entries(values.secondaryFunders || {})
      .filter(([id]) => id !== funderId) // Exclude current funder
      .map(([_, f]) => f.lender?.id)
      .filter(Boolean);

    return allUnderwriters.filter((lender) => lender.id !== primaryFunderId && !selectedSecondaryIds.includes(lender.id));
  };

  const getCurrentError = () => {
    return errors.secondaryFunders?.[funderId]?.lender?.id;
  };

  const getCurrentTouched = () => {
    return touched.secondaryFunders?.[funderId]?.lender?.id;
  };

  const renderInput = (params: any) => (
    <TextField
      {...params}
      fullWidth
      placeholder="Select Secondary Underwriter"
      inputRef={inputReference}
      name={`secondaryFunders.${funderId}.lender`}
      onBlur={handleBlur}
      error={Boolean(getCurrentTouched() && getCurrentError())}
      helperText={getCurrentTouched() && getCurrentError()}
      InputProps={{
        ...params.InputProps,
        startAdornment: values.secondaryFunders?.[funderId]?.lender?.id && (
          <AssetLogo
            logo={values.secondaryFunders?.[funderId]?.lender?.logo}
            name={values.secondaryFunders?.[funderId]?.lender?.name}
            size={'sm'}
          />
        )
      }}
      sx={{
        '& fieldset': { border: 'none' },
        '& .MuiInputLabel-root': { padding: '14px 0 8px 16px' },
        '& .MuiOutlinedInput-root': { padding: '14px 0 8px 16px' }
      }}
    />
  );

 const renderOption = (props: any, option: Underwriter) => {
     const { key, ...rest } = props;
    return (
    <Box
      component="li"
      {...rest}
      key={key}
      sx={{
        display: 'flex',
        width: '100%',
        px: 1,
        py: 0.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      {
        <McaUnderwriterWidget
            underwriter={option}
        />
      }
    </Box>
  )};

  const handleChange = (event: SyntheticEvent, newValue: Underwriter | null) => {
    setFieldValue(`secondaryFunders.${funderId}.lender`, newValue || null );
    setShowInfo(!!newValue?.id);
  };

  const handleInputChange = (event: SyntheticEvent, newInputValue: string) => {
    setUnderwriterInputValue(newInputValue);
  };

  const handleClickAway = () => {
    setShowInfo(disabled ? false : !!values.secondaryFunders?.[funderId]?.lender?.id);
  };

  return (
    <Stack spacing={0.5} sx={{ width: '100%' }}>
      {values.secondaryFunders?.[funderId]?.lender?.id && showInfo ? (
        <Stack
          onClick={() => {
            if (disabled) return;
            setShowInfo(false);
            delay(() => inputReference.current?.focus(), 500);
          }}
          sx={{ px: 2 }}
        >
          {
            <McaUnderwriterWidget
              underwriter={values.secondaryFunders?.[funderId]?.lender}
            />
          }
        </Stack>
      ) : (
        <ClickAwayListener onClickAway={handleClickAway}>
          <Autocomplete
            disabled={disabled}
            id={id}
            getOptionLabel={(option: Underwriter) => `${option.name || ''}`}
            options={getAvailableUnderwriters(lenders)}
            autoComplete
            value={(values.secondaryFunders?.[funderId]?.lender?.id ? values.secondaryFunders?.[funderId]?.lender as Underwriter : null)}
            isOptionEqualToValue={(option: Underwriter, value: Underwriter) =>
              !!value?.id && option.id === value.id
            }
            onChange={handleChange}
            onInputChange={handleInputChange}
            renderInput={renderInput}
            renderOption={renderOption}
          />
        </ClickAwayListener>
      )}
      {getCurrentTouched() && getCurrentError() && (
        <div
          style={{
            color: '#d32f2f',
            fontSize: '0.75rem',
            marginLeft: '14px',
            marginTop: showInfo ? '8px' : '-4px'
          }}
        >
          {getCurrentError()}
        </div>
      )}
    </Stack>
  );
};

export default SecondaryFunderAutocomplete;
